import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import { AppInterface } from "../../store/community/appsSlice";
import appMakerUrls from "../../configs/community/communityUrls";
import { settingEntity, settingsEntity } from "./settingsActions";
import {SettingInterface} from "../../store/community/settingsSlice";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: appMakerUrls.appPath(value.id),
    main_bundle_url: appMakerUrls.bundlePathBy(value.id, value.main_bundle_id),
    icon_url: appMakerUrls.remoteUrl(value.icon_url),
    bundle_icon_url: appMakerUrls.remoteUrl(value.bundle_icon_url)
  };
}

export const appsEntity = new schema.Entity(
  'apps',
  {
    setting: settingsEntity
  },
  {
    processStrategy
  }
);

export const appEntity = new schema.Entity(
  'app',
  {
    setting: settingEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      setting: { [key: string]: SettingInterface };
      app: AppInterface;
    }
  >(response.data.app, appEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized  = normalize<
    any,
    {
      settings: { [key: string]: SettingInterface };
      apps: { [key: string]: AppInterface },
    }
    >(response.data.apps, [appsEntity]);

  return {
    settings: normalized.entities?.settings || {},
    apps: normalized.entities?.apps || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchApps = createAsyncThunk(
  `${PREFIX}_apps/fetchApps`,
  async (params: object = {}) => {
    const response = await communityApi.get(`/onesky/apps`, { params })
    return normalizeCollection(response);
  }
);

export const createApp = createAsyncThunk(
  `${PREFIX}_apps/createApp`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await communityApi.post(`/onesky/apps`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const fetchApp = createAsyncThunk(
  `${PREFIX}_apps/fetchApp`,
  async (id: number | string) => {
    const response = await communityApi.get(`/onesky/apps/${id}`);
    return normalizeInstance(response);
  }
);

export const updateApp = createAsyncThunk(
  `${PREFIX}_apps/updateApp`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/apps/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const updateAppBundle = createAsyncThunk(
  `${PREFIX}_apps/updateApp`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/apps/${id}/bundle`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const feedApp = (
  id: number,
  params: { second_app_id: number, target: string }
) => communityApi.post(`/onesky/apps/${id}/feed`, params);
